import { useMutation, useQueryClient } from '@tanstack/react-query';
import { cacheKeys, useFetcher } from '../../graphql';

export interface SetWorkspaceSmsEnabledInput {
  workspaceId: string;
  enabled: boolean;
}

export interface SetWorkspaceSmsEnabledResult {
  workspaceId: string;
  smsEnabled: boolean;
}

interface SetWorkspaceSmsEnabledResponse {
  workspace: {
    setWorkspaceSmsEnabled: SetWorkspaceSmsEnabledResult;
  };
}

/**
 * Enable/disable texting callers back on PHONE-origin Activities (Communication
 * settings — plan 0026 Phase 1). Rides on the workspace's existing phone
 * number (plan 0025) rather than provisioning anything of its own — the
 * backend rejects enabling without a `phoneNumber` set. Requires global admin
 * on the backend (`*.*`); gate the UI entry point with the same permission.
 */
export const useSetWorkspaceSmsEnabled = () => {
  const queryClient = useQueryClient();
  const fetcherFn = useFetcher<
    SetWorkspaceSmsEnabledResponse,
    { input: SetWorkspaceSmsEnabledInput }
  >(SET_WORKSPACE_SMS_ENABLED);

  return useMutation({
    mutationFn: async (input: SetWorkspaceSmsEnabledInput) => {
      const data = await fetcherFn({ input });
      return data.workspace.setWorkspaceSmsEnabled;
    },
    onSuccess: async (result) => {
      await queryClient.invalidateQueries({
        queryKey: cacheKeys('workspace').detail(result.workspaceId),
      });
    },
  });
};

const SET_WORKSPACE_SMS_ENABLED = `
  mutation SetWorkspaceSmsEnabled($input: SetWorkspaceSmsEnabledInput!) {
    workspace {
      setWorkspaceSmsEnabled(input: $input) {
        workspaceId
        smsEnabled
      }
    }
  }
`;
